import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {useGetAppQuery, useGetAppVersionsQuery} from '../../api/appApiSlice';
import versionCompare from '../../services/versionCompare';
import Block from '../Block';
import { Loader } from '../components';
import FilterButton, { FilterGroup } from '../FilterButton';
import DevicesCell from '../metrics/tables/cells/DevicesCell';
import EnabledBadge from '../metrics/tables/cells/EnabledCell';
import OptInRateCell from '../metrics/tables/cells/OptInRateCell';
import Table from '../tables/Table';
import TableSearch from '../tables/TableSearch';
import useCustomTable from '../tables/useCustomTable';
import Pagination from "../tables/Pagination";

const SdkVersionTable = ({appId, filter, onRowClick, extraColumns=[]}) => {
  const { data = [], isSuccess, isLoading, isError, refetch } = useGetAppVersionsQuery({appId, filter})
  const { data: {environment: app_environment} = {}, isAppSuccess } = useGetAppQuery({appId, filter})
  const tableData = useMemo(()=>{
    return data.map((row) => {
      return {
        ...row,
        environment: row.production_date ? 'production' : 'test',
        opt_in_geolocated_devices: row.location_authorized_devices/row.active_devices,
        prev_opt_in_geolocated_devices: row.prev_location_authorized_devices/row.prev_active_devices,
        loc_consent_rate: row.loc_consented_devices/row.location_authorized_devices,
        prev_loc_consent_rate: row.prev_loc_consented_devices/row.prev_location_authorized_devices,
        consent_rate: row.location_authorized_devices/row.consented_devices,
        prev_consent_rate: row.prev_location_authorized_devices/row.prev_consented_devices,
      }
    })
  }, [data])
  const { t } = useTranslation();
  const toFloat = (val) => {
    if (isNaN(val)) { return -1 }
    return parseFloat(val)
  }

  const {
    tableProps,
    paginationProps,
    tableFilterProps,
    tableSearchProps
  } = useCustomTable({
    sortable: true,
    paginate: true,
    sortBy: {app_version: 'desc'}
  });
  const {filters, setAllFilters} = tableFilterProps

  const columns = useMemo(() => [{
    Header: t("app.app_version"),
    accessor: 'app_version',
    sortType: (rowA, rowB, id, desc) => (versionCompare(rowA.original.app_version,rowB.original.app_version))
  },{
    Header: t("app.sdk_version"),
    accessor: 'sdk_version',
    sortType: (rowA, rowB, id, desc) => (versionCompare(rowA.original.sdk_version,rowB.original.sdk_version))
  },{
    Header: t("app.environment"),
    accessor: 'environment',
    format: 'capitalize'
  },{
    Header: t("app.test_date"),
    accessor: 'test_date',
    format: 'date'
  },{
    Header: t("app.production_date"),
    accessor: 'production_date',
    format: 'date'
  },{
    Header: t("app.cmp_approved"),
    Cell: ({row}) => (<EnabledBadge enabled={row.original.cmp_approved} />),
    accessor: 'cmp_approved',
  },{
    Header: t("app.cmp_name"),
    accessor: 'cmp_name',
  },{
    Header: t("stats.devices"),
    Cell: ({row}) => (<DevicesCell devices={row.original.active_devices} refDevices={row.original.prev_active_devices} />),
    accessor: 'active_devices',
  },{
    Header: t("stats.geolocated_devices"),
    Cell: ({row}) => (<DevicesCell devices={row.original.location_authorized_devices} refDevices={row.original.prev_location_authorized_devices} />),
    accessor: 'geolocated_devices',
  },{
    Header: t("stats.opt_in_geolocated_devices"),
    Cell: ({row}) => (
        <OptInRateCell val={row.original.opt_in_geolocated_devices} refVal={row.original.prev_opt_in_geolocated_devices} />
      ),
    accessor: 'opt_in_geolocated_devices',
    sortType: (rowA, rowB, id, desc) => (Math.sign(rowB.original.opt_in_geolocated_devices - rowA.original.opt_in_geolocated_devices))
  },{
    Header: t("stats.consent"),
    Cell: ({row}) => (<DevicesCell devices={row.original.loc_consented_devices} refDevices={row.original.prev_loc_consented_devices} />),
    accessor: 'loc_consented_devices',
  },{
    Header: t("stats.consent_rate"),
    Cell: ({row}) => (
        <OptInRateCell val={row.original.loc_consent_rate} refVal={row.original.prev_loc_consent_rate} />
      ),
    accessor: 'loc_consent_rate',
    sortType: (rowA, rowB, id, desc) => (Math.sign(toFloat(rowB.original.loc_consent_rate) - toFloat(rowA.original.loc_consent_rate)))
  },
  ...extraColumns
  ]);

  return (
    <>
      <Block title={t('app.sdk_versions')}>
        <FilterGroup {...tableFilterProps}>
          <FilterButton
            title={t("publisher.environment")}
            options={["Test", "Production"]}
            search={true}
            includeAll={true}
            selected={app_environment == 'production' && ["Production"]}
            name="environment"/>
          <TableSearch {...tableSearchProps} style={{float: "right"}}/>
        </FilterGroup>
        <Loader success={!isLoading && isSuccess} error={!isLoading && !isSuccess} retry={refetch}>
          <Table
            noPadding
            data={tableData}
            columns={columns}
            {...tableProps}
            onRowClick={onRowClick}
            initialFilters={app_environment == 'production' && [{id: "environment", value: ["Production"]}]} />
          <Pagination {...paginationProps}/>
        </Loader>
      </Block>
    </>
  );
}

export default SdkVersionTable
